// Сезоны (Турниры): регистрация/вход/выход через СУБ-АККАУНТ (свой JWT, изолирован от основного).
// Вход = подмена активного JWT (cookie 'jwt' + axios-заголовок + store.auth.jwt) на сезонный, с бэкапом
// основного токена (в Vuex + localStorage — переживает перезагрузку страницы, см. RESTORE_SEASON).
// Все существующие экраны (PVP/Pack/Wheel/Market) переиспользуются «как есть»: бэк уже изолирует данные по
// owner_id суб-аккаунта, фронту достаточно один раз подменить токен на время сезонной сессии.
import { t, currentLocale } from '@/i18n';
import { serverError } from '@/i18n/serverErrors';
// 🔴 Сезонная подмена активного токена — тоже через `@/session` (иначе в мини-аппе
// «вошёл в турнир, перезагрузил — играешь основным аккаунтом»).
import { applyAuthHeader, getJwt, setJwt } from '@/session';

const LS_MAIN = 'cz_season_main_jwt';
const LS_ID = 'cz_season_id';
const LS_TITLE = 'cz_season_title';

const state = {
  seasons: [],
  loading: false,
  error: null,
  active: false,
  seasonId: null,
  seasonTitle: null,
  seasonState: null, // текущий статус сезона (registration|active|finished|archived) — гейт «Найти соперника»
  seasonInfo: null,  // карточка активного сезона (даты этапов) — для баннера-обратного-отсчёта
  seasonBoard: [],   // турнирная таблица активного сезона (для локального лидерборда в ПВП)
  mainToken: null, // бэкап основного JWT — на него возвращаемся по «Выйти»
};

const getters = {};

const mutations = {
  SET_SEASONS: (state, list) => { state.seasons = list; },
  SET_LOADING: (state, v) => { state.loading = v; },
  SET_ERROR: (state, e) => { state.error = e; },
  SET_SEASON_STATE: (state, v) => { state.seasonState = v; },
  SET_SEASON_INFO: (state, info) => { state.seasonInfo = info; if (info && info.state) state.seasonState = info.state; },
  SET_SEASON_BOARD: (state, rows) => { state.seasonBoard = rows || []; },
  SEASON_ENTER: (state, { seasonId, title, mainToken, seasonState }) => {
    state.active = true;
    state.seasonId = seasonId;
    state.seasonTitle = title;
    if (seasonState !== undefined) state.seasonState = seasonState;
    state.mainToken = mainToken;
  },
  SEASON_EXIT: (state) => {
    state.active = false;
    state.seasonId = null;
    state.seasonTitle = null;
    state.seasonState = null;
    state.seasonInfo = null;
    state.seasonBoard = [];
    state.mainToken = null;
  },
};

// Подменить активный токен на сезонный (cookie + axios-заголовок + store.auth.jwt); бэкап основного токена —
// из ТЕКУЩЕГО состояния (если уже в сезоне — берём уже сохранённый, не перезаписываем сезонным по ошибке).
function swapToken(context, inst, token) {
  const mainToken = context.state.active
    ? context.state.mainToken
    : (inst.$store.state.auth.jwt || getJwt());
  setJwt(token);
  applyAuthHeader(inst.axios, token);
  context.commit('SET_JWT', token); // модули НЕ namespaced (как auth/pvp) — общее пространство мутаций
  return mainToken;
}

function persist(mainToken, seasonId, title) {
  localStorage.setItem(LS_MAIN, mainToken);
  localStorage.setItem(LS_ID, String(seasonId));
  localStorage.setItem(LS_TITLE, title || '');
}

const actions = {
  FETCH_SEASONS: async (context, inst) => {
    // Спиннер только на ПЕРВОЙ загрузке; фоновый опрос (список уже есть) — тихо, без мигания «Загрузка…».
    const silent = context.state.seasons.length > 0;
    if (!silent) context.commit('SET_LOADING', true);
    context.commit('SET_ERROR', null);
    try {
      const r = await inst.axios.get('/seasons/', { params: { lang: currentLocale() } });
      context.commit('SET_SEASONS', r.data);
    } catch (e) {
      const detail = e.response && e.response.data && e.response.data.detail;
      context.commit('SET_ERROR', detail ? serverError(detail) : t('tour.errLoad'));
    }
    if (!silent) context.commit('SET_LOADING', false);
  },
  // Зарегистрироваться в сезоне (создаёт суб-аккаунт на бэке) и сразу войти. seasonState — статус на момент
  // входа (registration при регистрации; «Найти соперника» разблокируется, когда станет active — см. REFRESH).
  JOIN_SEASON: async (context, { inst, seasonId, title, seasonState }) => {
    const r = await inst.axios.post(`/seasons/${seasonId}/join`);
    const mainToken = swapToken(context, inst, r.data.token);
    context.commit('SEASON_ENTER', { seasonId, title, mainToken, seasonState });
    persist(mainToken, seasonId, title);
    return r.data;
  },
  // Войти в УЖЕ существующий суб-аккаунт сезона (повторный вход; enter возможен только в active).
  ENTER_SEASON: async (context, { inst, seasonId, title, seasonState }) => {
    const r = await inst.axios.post(`/seasons/${seasonId}/enter`);
    const mainToken = swapToken(context, inst, r.data.token);
    context.commit('SEASON_ENTER', { seasonId, title, mainToken, seasonState: seasonState || 'active' });
    persist(mainToken, seasonId, title);
    return r.data;
  },
  // Освежить активный сезон: статус (гейт «Найти соперника»), даты этапов (баннер-отсчёт) и турнирную
  // таблицу (локальный лидерборд в ПВП). Сезонный саб не видит себя в общем /seasons — берём из лидерборда.
  REFRESH_SEASON_STATE: async (context, inst) => {
    if (!context.state.seasonId) return;
    try {
      const r = await inst.axios.get(`/seasons/${context.state.seasonId}/leaderboard`, { params: { lang: currentLocale() } });
      if (r.data && r.data.season) {
        context.commit('SET_SEASON_INFO', r.data.season);
        if (r.data.season.title) context.commit('SET_SEASON_STATE', r.data.season.state);
      }
      context.commit('SET_SEASON_BOARD', (r.data && r.data.rows) || []);
    } catch (e) { /* сеть/архив — не критично, оставляем текущее состояние */ }
  },
  // Вернуться на основной аккаунт (восстановить бэкап токена).
  EXIT_SEASON: (context, inst) => {
    const mainToken = context.state.mainToken || localStorage.getItem(LS_MAIN);
    if (mainToken) {
      setJwt(mainToken);
      applyAuthHeader(inst.axios, mainToken);
      context.commit('SET_JWT', mainToken);
    }
    context.commit('SEASON_EXIT');
    localStorage.removeItem(LS_MAIN);
    localStorage.removeItem(LS_ID);
    localStorage.removeItem(LS_TITLE);
  },
  // Восстановить активную сезонную сессию после перезагрузки страницы (Vuex-состояние в памяти теряется,
  // а cookie 'jwt' уже содержит сезонный токен — TRY_AUTH его подхватит сам; здесь только поднимаем баннер/бэкап).
  RESTORE_SEASON: (context) => {
    const mainToken = localStorage.getItem(LS_MAIN);
    const seasonId = localStorage.getItem(LS_ID);
    const title = localStorage.getItem(LS_TITLE);
    if (mainToken && seasonId) {
      context.commit('SEASON_ENTER', { seasonId: Number(seasonId), title, mainToken });
    }
  },
};

export default {
  state,
  getters,
  mutations,
  actions,
};
